import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "217",
  "slug": "run-geography-competition-school",
  "title": "How to Run a Geography Competition at Your School",
  "excerpt": "Geography bees and school geography competitions build real skills, generate school-wide interest in the subject, and are simpler to organize than most teachers expect. Here is a complete planning guide.",
  "date": "2026-03-21",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1544531585-9847b68c8c86?w=800&q=80",
  "readTime": "4 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Geography competitions — whether framed as a geography bee, a school championship, or a class tournament — have a remarkable track record for building enthusiasm around a subject that often struggles to compete for student attention. They create visible stakes, they reward genuine geographic knowledge in public, and they generate school-wide conversations about the world in a way that unit tests and projects rarely do. They are also, with the right tools and a little planning, straightforward to run. This guide covers everything from initial planning to the competition day itself."
    },
    {
      "type": "heading",
      "text": "Choosing Your Format"
    },
    {
      "type": "paragraph",
      "text": "The format you choose will depend on your school's size, available time, and whether you want to emphasize individual or team competition. Here are the three formats that work best in K-12 settings:"
    },
    {
      "type": "list",
      "items": [
        "Individual bracket tournament: Students compete one-on-one in elimination rounds. Works well for a geography bee format, especially in middle school. Clear winner, easy to follow for spectators, builds significant individual prestige for the winner.",
        "Team relay competition: Groups of three to five students compete as teams, with each round requiring a different team member to answer. Reduces individual performance pressure, encourages collaborative preparation, works well for larger cohorts.",
        "EarthGuessr class championship: Use EarthGuessr's multiplayer mode across multiple class sessions, tracking cumulative scores over several weeks. Less formal, more sustained, and ideal for building school-wide participation without a single high-pressure event. Can culminate in a finals day."
      ]
    },
    {
      "type": "heading",
      "text": "Using EarthGuessr for the Competition"
    },
    {
      "type": "paragraph",
      "text": "EarthGuessr's multiplayer mode is particularly well-suited for school geography competitions. A teacher or organizer creates a room, sets the round parameters, and all participants compete simultaneously on the same satellite images. Scores are tracked in real time and displayed on a leaderboard visible to all players. For a finals day, projecting the EarthGuessr globe on a large screen while students compete is genuinely theatrical — the class can watch all guesses land on the globe simultaneously, with gasps or cheers when a student places a pin in exactly the right location."
    },
    {
      "type": "paragraph",
      "text": "A satellite-location round asks students to interpret an image as well as locate a place. If you want to recognise those skills separately, award credit for a labelled feature or an explanation alongside the location score."
    },
    {
      "type": "heading",
      "text": "Planning Timeline"
    },
    {
      "type": "list",
      "items": [
        "Six weeks before: Announce the competition and format to students. Share practice resources — EarthGuessr's daily challenge and streak mode are ideal for independent preparation. Post a sign-up sheet.",
        "Four weeks before: Run the first qualifying round in class. This can be a single EarthGuessr multiplayer session; the top scorers advance to the semifinal. Aim for 12 to 20 finalists depending on your school's size.",
        "Two weeks before: Run semifinal rounds. If using an individual bracket format, pair students and run head-to-head rounds. If using team format, assemble teams and run two or three practice sessions.",
        "One week before: Confirm venue, technology setup, and logistics. If projecting EarthGuessr on a screen for a live audience, test the setup in advance.",
        "Competition day: Allow extra setup time. Have a backup device and internet connection ready. Brief spectators on how EarthGuessr works so they can follow the action."
      ]
    },
    {
      "type": "heading",
      "text": "Making It Feel Like an Event"
    },
    {
      "type": "paragraph",
      "text": "The difference between a geography activity and a geography competition is atmosphere. A few simple additions transform a multiplayer EarthGuessr session into something that feels genuinely significant:"
    },
    {
      "type": "list",
      "items": [
        "Invite an audience: even 20 to 30 students watching from the side of the room changes the energy completely.",
        "Use a leaderboard display: project the live EarthGuessr leaderboard on a screen visible to all participants and spectators.",
        "Create a competition bracket: print or display the bracket so students can see the path to the final.",
        "Offer small prizes: certificates, geography books, or the simple prestige of being named the school's geography champion. The prize matters less than the public recognition.",
        "Announce the winners school-wide: a mention in the school newsletter or a photo on the school's social media gives winners genuine recognition and signals to the whole school that geographic achievement is valued.",
        "Make it annual: the first year builds interest; the second year creates tradition; the third year creates a school-wide culture around geographic excellence."
      ]
    },
    {
      "type": "heading",
      "text": "Connecting the Competition to Curriculum"
    },
    {
      "type": "paragraph",
      "text": "Tie the preparation to a topic from the course and assess it separately from the tournament. Ask students to explain a new image after practice so the competition result is not your only evidence."
    },
    {
      "type": "paragraph",
      "text": "Consider framing the competition as the culminating event of a geography unit, or as an annual tradition that spans the year. Either way, the competition signals something important to students: that geographic knowledge is worth developing, worth showing off, and worth celebrating. In a subject that too often gets reduced to memorizing capitals, a well-run competition is a reminder that geography is one of the most interesting ways to understand the world we actually live in."
    }
  ]
};
